import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from "@/components/theme-provider";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel, DropdownMenuGroup, DropdownMenuShortcut } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bell, User, Menu, X, Check, ChevronsUpDown, ChevronDown, Settings, Plus, HelpCircle, LogOut } from "lucide-react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import Logo from "@/components/logo";
import { useAuth } from "../context/AuthContext";
import { HiOutlineSparkles } from "react-icons/hi";
import AICommandPalette, { contextHelpers } from "@/components/AiCommandPalette";
import { Toaster } from "@/components/ui/toaster";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import NotificationCenter from "@/components/NotificationCenter";
import { Label } from "@/components/ui/label";
import { useAiPageContext } from "@/context/AiPageContextProvider";
// import statusBotCircleIcon from "../assets/status-bot-circle-icon.svg";
import statusBotIcon from "../assets/status-bot-icon.svg";

const Layout = ({ children, noContainer = false }) => {
    const [isNavVisible, setIsNavVisible] = useState(true);
    const [lastScrollY, setLastScrollY] = useState(0);
    const { theme, setTheme } = useTheme();
    const { logout, user, activeOrganization, setActiveOrganization, axiosInstance, unreadCount } = useAuth();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
    const [organizations, setOrganizations] = useState([]);
    const location = useLocation();
    const navigate = useNavigate();
    const { organizationSlug } = useParams();
    const currentOrgSlug = organizationSlug || activeOrganization?.slug;
    const [isLoadingOrg, setIsLoadingOrg] = useState(true);
    const [activeSection, setActiveSection] = useState('');
    const [isNotificationCenterOpen, setIsNotificationCenterOpen] = useState(false);
    const { currentAiPageContext, setCurrentAiPageContext } = useAiPageContext();
    // Track if we've set a default context
    const [hasSetDefaultContext, setHasSetDefaultContext] = useState(false);

    useEffect(() => {
        const fetchOrganizations = async () => {
            setIsLoadingOrg(true);
            try {
                const response = await axiosInstance.get('/organizations');
                setOrganizations(response.data);

                if (currentOrgSlug && response.data.length > 0) {
                    const orgExists = response.data.some(org => org.slug === currentOrgSlug);
                    if (!orgExists) {
                        setActiveOrganization(response.data[0]);
                    }
                } else if (!currentOrgSlug && response.data.length > 0) {
                    setActiveOrganization(response.data[0]);
                }
            } catch (error) {
                console.error('Error fetching organizations:', error);
            } finally {
                setIsLoadingOrg(false);
            }
        };
        fetchOrganizations();
    }, []);

    useEffect(() => {
        if (organizationSlug && organizations.length > 0) {
            try {
                if (organizationSlug !== activeOrganization?.slug || activeOrganization?.name !== organizations.find(org => org.slug === organizationSlug)?.name) {
                    setActiveOrganization(organizations.find(org => org.slug === organizationSlug));
                }
            } catch (error) {
                console.error('Error setting active organization:', error);
            }
        }
    }, [organizationSlug, organizations]);

    useEffect(() => {
        const controlNavbar = () => {
            if (typeof window !== 'undefined') {
                if (window.scrollY <= 20) {
                    setIsNavVisible(true);
                } else if (window.scrollY > lastScrollY) {
                    setIsNavVisible(false);
                } else {
                    setIsNavVisible(true);
                }

                setLastScrollY(window.scrollY);
            }
        };

        const handleResize = () => {
            if (window.innerWidth >= 768) { // md breakpoint
                setIsMobileMenuOpen(false);
                document.body.style.overflow = 'unset';
            }
        };

        if (typeof window !== 'undefined') {
            window.addEventListener('scroll', controlNavbar);
            window.addEventListener('resize', handleResize);

            return () => {
                window.removeEventListener('scroll', controlNavbar);
                window.removeEventListener('resize', handleResize);
            };
        }
    }, [lastScrollY]);

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
        document.body.style.overflow = !isMobileMenuOpen ? 'hidden' : 'unset';
    };

    const isLinkActive = (path) => {
        // Handle root path and today page
        if (path === '/') {
            return location.pathname === '/today';
        }

        // First check if the path matches exactly (for non-org-scoped routes)
        if (location.pathname.startsWith(path)) {
            return true;
        }

        // Then check for org-scoped routes
        const pathParts = location.pathname.split('/');
        if (pathParts.length >= 2) {
            // Remove the organization slug and rejoin
            const pathWithoutOrg = ['', ...pathParts.slice(2)].join('/');

            // Check if this path starts with the given path
            return pathWithoutOrg.startsWith(path);
        }

        return false;
    };

    const handleKeyDown = useCallback((event) => {
        // Check for CMD+K (Mac) or Ctrl+K (Windows/Linux)
        if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
            event.preventDefault();
            setIsCommandPaletteOpen(prev => !prev);
        }
    }, []);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);

    useEffect(() => {
        // Only set organization context if:
        // 1. We have an active organization
        // 2. No context exists yet (this is a default fallback)
        // 3. We haven't already set a default (to prevent loops)
        if (activeOrganization && !currentAiPageContext && !hasSetDefaultContext) {
            setCurrentAiPageContext(
                contextHelpers.createOrganizationContext(activeOrganization)
            );
            setHasSetDefaultContext(true);
        }
        
        // Reset our flag when currentAiPageContext is cleared (like when navigating to a new page)
        if (!currentAiPageContext) {
            setHasSetDefaultContext(false);
        }
    }, [activeOrganization, currentAiPageContext, hasSetDefaultContext, setCurrentAiPageContext]);

    return (
        <div className="min-h-[calc(100vh-4rem)] min-w-full bg-background">
            <nav className={`border-b fixed top-0 left-0 right-0 z-50 bg-background transition-all duration-300 ${isNavVisible ? 'translate-y-0' : '-translate-y-full'
                } ${isMobileMenuOpen ? 'h-screen md:h-auto' : ''}`}>
                <div className="mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center">
                            <Logo className="mr-8" />
                            <div className="hidden md:flex space-x-4">
                                <Button
                                    variant={isLinkActive('/') ? "secondary" : "ghost"}
                                    onClick={() => navigate('/today')}
                                >
                                    Today
                                </Button>
                                <Button
                                    variant={isLinkActive('/plan') ? "secondary" : "ghost"}
                                    onClick={() => navigate('/plan')}
                                >
                                    Planner
                                </Button>
                                <Button
                                    variant={isLinkActive('/monitors') ? "secondary" : "ghost"}
                                    onClick={() => navigate('/monitors')}
                                >
                                    Monitor
                                </Button>
                                <Button
                                    variant={isLinkActive('/forms') ? "secondary" : "ghost"}
                                    onClick={() => navigate('/forms')}
                                >
                                    Form
                                </Button>
                                {/* <Button
                                    variant={isLinkActive('/reports') ? "secondary" : "ghost"}
                                    onClick={() => navigate('/reports')}
                                >
                                    Reports
                                </Button> */}
                            </div>
                        </div>
                        <div className="flex items-center space-x-2">
                            {/* Notifications */}
                            <Button variant="ghost" size="icon" onClick={() => setIsNotificationCenterOpen(true)}>
                                <div className="relative">
                                    <Bell className="h-5 w-5" />
                                    {unreadCount > 0 && (
                                        <div className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 bg-primary rounded-full flex items-center justify-center">
                                            <span className="text-[10px] font-medium text-primary-foreground">
                                                {unreadCount}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </Button>

                            {/* User Menu with Organization */}
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="outline" className="hidden md:flex items-center gap-2">
                                        <div className="flex flex-col items-end">
                                            {activeOrganization?.name ? (
                                                <span className="text-sm font-medium">{activeOrganization?.name}</span>
                                            ) : (
                                                <Skeleton className="h-4 w-[100px]" />
                                            )}
                                        </div>
                                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-60">
                                    {/* Current Organization Section */}
                                    <DropdownMenuLabel className="text-xs text-muted-foreground">Current Organization</DropdownMenuLabel>
                                    <DropdownMenuItem onClick={() => navigate(`/${currentOrgSlug}/settings`)}>
                                        <Settings className="mr-2 h-4 w-4" />
                                        Organization Settings
                                    </DropdownMenuItem>

                                    {/* Switch Organization */}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuLabel className="text-xs text-muted-foreground">
                                        Switch Organization
                                    </DropdownMenuLabel>
                                    <DropdownMenuGroup>
                                        {organizations.map((org) => (
                                            <DropdownMenuItem
                                                key={org.id}
                                                className="flex items-center gap-2"
                                                onClick={() => navigate(`/${org.slug}`)}
                                            >
                                                <Avatar className="h-5 w-5">
                                                    <AvatarFallback className="bg-zinc-200 text-zinc-800 dark:bg-zinc-700 dark:text-zinc-200 text-medium text-[0.55rem]">
                                                        {org.name.slice(0, 2).toUpperCase()}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <span className="flex-1 truncate">{org.name}</span>
                                                {org.slug === currentOrgSlug && (
                                                    <Check className="h-4 w-4 text-primary" />
                                                )}
                                            </DropdownMenuItem>
                                        ))}
                                        <DropdownMenuItem onClick={() => navigate('/organizations/new')}>
                                            <Plus className="mr-2 h-4 w-4" />
                                            Create Organization
                                        </DropdownMenuItem>
                                    </DropdownMenuGroup>

                                    {/* User Section */}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuLabel className="text-xs text-muted-foreground">Account</DropdownMenuLabel>
                                    <DropdownMenuItem onClick={() => navigate('/preferences')}>
                                        <User className="mr-2 h-4 w-4" />
                                        Preferences
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => navigate('/support')}>
                                        <HelpCircle className="mr-2 h-4 w-4" />
                                        Help & Support
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={logout}>
                                        <LogOut className="mr-2 h-4 w-4" />
                                        Logout
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                            <Button variant="ghost" size="icon" className="md:hidden" onClick={toggleMobileMenu}>
                                {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                            </Button>
                        </div>
                    </div>

                    {/* Mobile menu */}
                    <div className={`md:hidden transition-all duration-300 ease-in-out ${isMobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}>
                        <div className="py-4 space-y-4">
                            {/* Organization Selector */}
                            <div className="px-2 mb-4">
                                <Label className="text-xs text-muted-foreground mb-2">Current Organization</Label>
                                <Select
                                    value={currentOrgSlug}
                                    onValueChange={(slug) => {
                                        navigate(`/${slug}`);
                                        toggleMobileMenu();
                                    }}
                                >
                                    <SelectTrigger className="w-full">
                                        <SelectValue>
                                            {activeOrganization?.name || <Skeleton className="h-4 w-[100px]" />}
                                        </SelectValue>
                                    </SelectTrigger>
                                    <SelectContent>
                                        {organizations.map((org) => (
                                            <SelectItem key={org.id} value={org.slug}>
                                                <div className="flex items-center gap-2">
                                                    <Avatar className="h-5 w-5">
                                                        <AvatarFallback className="bg-zinc-200 text-zinc-800 dark:bg-zinc-700 dark:text-zinc-200 text-medium text-[0.55rem]">
                                                            {org.name.slice(0, 2).toUpperCase()}
                                                        </AvatarFallback>
                                                    </Avatar>
                                                    <span className="flex-1 truncate">{org.name}</span>
                                                </div>
                                            </SelectItem>
                                        ))}
                                        <SelectItem value="new" className="text-primary">
                                            <div className="flex items-center gap-2">
                                                <Plus className="h-4 w-4" />
                                                Create Organization
                                            </div>
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Navigation Links */}
                            <div className="space-y-1">
                                <Button
                                    variant={isLinkActive('/') ? "secondary" : "ghost"}
                                    className="w-full justify-start"
                                    onClick={() => { navigate('/today'); toggleMobileMenu(); }}
                                >
                                    Today
                                </Button>
                                <Button
                                    variant={isLinkActive('/monitors') ? "secondary" : "ghost"}
                                    className="w-full justify-start"
                                    onClick={() => { navigate('/monitors'); toggleMobileMenu(); }}
                                >
                                    Monitor
                                </Button>
                                <Button
                                    variant={isLinkActive('/plan') ? "secondary" : "ghost"}
                                    className="w-full justify-start"
                                    onClick={() => { navigate('/plan'); toggleMobileMenu(); }}
                                >
                                    Planner
                                </Button>
                                <Button
                                    variant={isLinkActive('/forms') ? "secondary" : "ghost"}
                                    className="w-full justify-start"
                                    onClick={() => { navigate('/forms'); toggleMobileMenu(); }}
                                >
                                    Form
                                </Button>
                                {/* <Button
                                    variant={isLinkActive('/reports') ? "secondary" : "ghost"}
                                    className="w-full justify-start"
                                    onClick={() => { navigate('/reports'); toggleMobileMenu(); }}
                                >
                                    Reports
                                </Button> */}
                            </div>

                            {/* Account Links */}
                            <div className="border-t pt-4 mt-4 space-y-1">
                                <Button
                                    variant="ghost"
                                    className="w-full justify-start"
                                    onClick={() => { navigate('/preferences'); toggleMobileMenu(); }}
                                >
                                    <User className="mr-2 h-4 w-4" />
                                    Preferences
                                </Button>
                                <Button
                                    variant="ghost"
                                    className="w-full justify-start"
                                    onClick={() => { navigate('/support'); toggleMobileMenu(); }}
                                >
                                    <HelpCircle className="mr-2 h-4 w-4" />
                                    Help & Support
                                </Button>
                                <Button
                                    variant="ghost"
                                    className="w-full justify-start text-red-500 hover:text-red-500 hover:bg-red-500/10"
                                    onClick={() => { logout(); toggleMobileMenu(); }}
                                >
                                    <LogOut className="mr-2 h-4 w-4" />
                                    Logout
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </nav>
            <main className={`mt-16 ${noContainer ? '' : 'mx-auto px-4 sm:px-6 lg:px-8 py-8 h-[calc(100vh-4rem)]'}`}>
                {children}
            </main>
            <Button
                className="fixed bottom-8 right-8 w-16 h-16 rounded-full bg-transparent hover:bg-transparent transition-transform hover:scale-110"
                size="icon"
                onClick={() => setIsCommandPaletteOpen(true)}
            >
                <img src={statusBotIcon} alt="AI Assistant" className="w-16 h-16" />
            </Button>
            <AICommandPalette 
                isOpen={isCommandPaletteOpen} 
                onClose={() => setIsCommandPaletteOpen(false)} 
                pageContext={currentAiPageContext}
            />
            <Toaster />
            <NotificationCenter
                open={isNotificationCenterOpen}
                setOpen={setIsNotificationCenterOpen}
                onOpenChange={setIsNotificationCenterOpen}
            />
        </div>
    );
};

export default Layout;
